import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { motion, AnimatePresence, useAnimation } from "framer-motion";
import { useAmazonContext } from "../../Contexts/AmazonContext";

const Pill = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 5px 10px 5px 8px;
  border-radius: 999px;
  border: 2px solid rgba(255, 255, 255, 0.85);
  background: ${(props) =>
    props.restored
      ? "linear-gradient(90deg, #ffb347, #ff6b35)"
      : "linear-gradient(90deg, #dff3ff, #b3e0ff)"};
  box-shadow: 0 3px 0 ${(props) => (props.restored ? "#d4541f" : "#6aa9d6")};
  cursor: pointer;
  font-family: "Quicksand", sans-serif;
  font-weight: 800;
  font-size: 13px;
  color: ${(props) => (props.restored ? "#ffffff" : "#12456e")};
  white-space: nowrap;
  position: relative;

  @media (max-width: 400px) {
    padding: 4px 8px 4px 6px;
    font-size: 12px;
  }
`;

const Icon = styled(motion.span)`
  font-size: 16px;
  line-height: 1;
`;

const Dots = styled.div`
  display: flex;
  gap: 3px;
`;

const Dot = styled(motion.span)`
  position: relative;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  background: ${(props) =>
    props.done ? "linear-gradient(145deg, #ffb347, #ff6b35)" : "rgba(255, 255, 255, 0.85)"};
  box-shadow: ${(props) =>
    props.done ? "0 0 6px rgba(255, 110, 40, 0.7)" : "inset 0 0 0 1.5px #8cc4ea"};

  @media (max-width: 400px) {
    width: 11px;
    height: 11px;
    font-size: 7px;
  }
`;

const Burst = styled(motion.span)`
  position: absolute;
  font-size: 14px;
  pointer-events: none;
`;

const PulseWrap = styled(motion.span)`
  display: flex;
  align-items: center;
  gap: 7px;
`;

const Count = styled.span`
  min-width: 26px;
  text-align: left;
`;

const StreakRecoveryTracker = () => {
  const { streakStatus, streakRecovery, setShowStreakFrozenModal } = useAmazonContext();
  const prevCompleted = useRef(streakRecovery?.lessonsCompleted ?? 0);
  const [burstIndex, setBurstIndex] = useState(null);
  const pulse = useAnimation();

  const completed = streakRecovery?.lessonsCompleted ?? 0;
  const required = streakRecovery?.lessonsRequired ?? 5;
  const restored = !!streakRecovery?.restored;

  useEffect(() => {
    if (completed > prevCompleted.current) {
      setBurstIndex(completed - 1);
      pulse.start({
        scale: [1, 1.18, 0.96, 1],
        rotate: restored ? [0, -6, 6, -3, 0] : 0,
        transition: { duration: 0.6 },
      });
      const id = setTimeout(() => setBurstIndex(null), 900);
      prevCompleted.current = completed;
      return () => clearTimeout(id);
    }
    prevCompleted.current = completed;
  }, [completed]);

  const visible = !!streakRecovery && (streakStatus === "frozen" || restored);

  return (
    <AnimatePresence>
      {visible && (
        <Pill
          type="button"
          key="streak-recovery-tracker"
          restored={restored}
          aria-label={`Streak frozen: ${completed} of ${required} lessons done to thaw it`}
          onClick={() => !restored && setShowStreakFrozenModal(true)}
          initial={{ opacity: 0, scale: 0.6, y: -6 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, scale: 1.4, transition: { duration: 0.5 } }}
          transition={{ duration: 0.45 }}
          whileTap={{ scale: 0.94 }}
        >
          <PulseWrap animate={pulse}>
            <Icon
              key={restored ? "fire" : "ice"}
              initial={{ rotate: -90, scale: 0 }}
              animate={{ rotate: 0, scale: 1 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              {restored ? "🔥" : "❄️"}
            </Icon>
            <Dots>
              {Array.from({ length: required }, (_, i) => {
                const done = i < completed;
                const justFilled = done && i >= prevCompleted.current;
                return (
                  <Dot
                    key={`${i}-${done}`}
                    done={done}
                    initial={justFilled ? { scale: 0.2 } : false}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 500, damping: 12 }}
                  >
                    {done ? "🔥" : ""}
                    <AnimatePresence>
                      {i === burstIndex && (
                        <Burst
                          initial={{ opacity: 1, y: 0, scale: 0.6 }}
                          animate={{ opacity: 0, y: -22, scale: 1.6 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.8 }}
                        >
                          🔥
                        </Burst>
                      )}
                    </AnimatePresence>
                  </Dot>
                );
              })}
            </Dots>
            <Count>{restored ? "Back!" : `${completed}/${required}`}</Count>
          </PulseWrap>
        </Pill>
      )}
    </AnimatePresence>
  );
};

export default StreakRecoveryTracker;
